/* NUO configurable mobile transitions for Prestige effect carousels. */
(() => {
  'use strict';

  if (window.NuoCarouselTransitionsInstalled) return;
  window.NuoCarouselTransitionsInstalled = true;

  const mobileMedia = window.matchMedia('(max-width: 699px)');
  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  const nativeCarousels = new WeakSet();

  const createGroupedControls = (animations) => {
    const finished = Promise.all(animations.map((animation) => animation.finished)).then(() => {
      window.setTimeout(() => animations.forEach((animation) => animation.cancel()), 0);
    });

    return {
      finished,
      finish: () => animations.forEach((animation) => animation.finish()),
    };
  };

  const createTransition = (fromSlide, toSlide, transition, direction) => {
    const duration = reducedMotionMedia.matches ? 0 : transition === 'fade' ? 240 : 280;
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const offset = direction === 'previous' ? -12 : 12;
    let fromFrames;
    let toFrames;

    if (transition === 'horizontal') {
      fromFrames = [
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: `translateX(${-offset}px)` },
      ];
      toFrames = [
        { opacity: 0, transform: `translateX(${offset}px)` },
        { opacity: 1, transform: 'translateX(0)' },
      ];
    } else {
      fromFrames = [{ opacity: 1 }, { opacity: 0 }];
      toFrames = [{ opacity: 0 }, { opacity: 1 }];
    }

    return createGroupedControls([
      fromSlide.animate(fromFrames, { duration, easing, fill: 'both' }),
      toSlide.animate(toFrames, { duration, easing, fill: 'both' }),
    ]);
  };

  const enhanceCarousel = (tagName) => {
    window.customElements.whenDefined(tagName).then((Carousel) => {
      const prototype = Carousel.prototype;
      if (prototype.nuoMobileTransitionsInstalled) return;

      const prestigeTransition = prototype.createOnChangeAnimationControls;

      Object.defineProperty(prototype, 'nuoMobileTransitionsInstalled', {
        value: true,
      });

      prototype.createOnChangeAnimationControls = function (fromSlide, toSlide, options = {}) {
        const transition = mobileMedia.matches ? this.getAttribute('mobile-transition') : 'vertical';

        if (transition !== 'fade' && transition !== 'horizontal') {
          return prestigeTransition.call(this, fromSlide, toSlide, options);
        }

        return createTransition(fromSlide, toSlide, transition, options.direction);
      };
    });
  };

  enhanceCarousel('text-with-icons-carousel');
  enhanceCarousel('testimonial-carousel');

  const setupNativeCarousel = (carousel) => {
    if (nativeCarousels.has(carousel)) return;
    nativeCarousels.add(carousel);

    const items = Array.from(carousel.children);
    let scrollTimer;

    const isNative = () => mobileMedia.matches && carousel.getAttribute('mobile-transition') === 'native';

    const currentScrollIndex = () => {
      if (carousel.clientWidth === 0) return carousel.selectedIndex;
      return Math.max(0, Math.min(Math.round(carousel.scrollLeft / carousel.clientWidth), items.length - 1));
    };

    const scrollToIndex = (index, behavior = 'smooth') => {
      carousel.scrollTo({
        left: index * carousel.clientWidth,
        behavior: reducedMotionMedia.matches ? 'auto' : behavior,
      });
    };

    const syncFromScroll = () => {
      scrollTimer = undefined;
      const index = currentScrollIndex();
      if (index !== carousel.selectedIndex) carousel.select(index, { instant: true });
    };

    const onScroll = () => {
      if (!isNative()) return;
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(syncFromScroll, 120);
    };

    const onScrollEnd = () => {
      if (!isNative()) return;
      window.clearTimeout(scrollTimer);
      syncFromScroll();
    };

    const onCarouselChange = (event) => {
      if (!isNative()) return;
      scrollToIndex(event.detail.index);
    };

    const onBreakpointChange = () => {
      if (isNative()) {
        scrollToIndex(carousel.selectedIndex, 'auto');
        if (carousel.hasAttribute('autoplay')) carousel.player?.resume(true);
      } else {
        window.clearTimeout(scrollTimer);
        carousel.scrollTo({ left: 0, behavior: 'auto' });
      }
    };

    carousel.addEventListener('scroll', onScroll, { passive: true });
    carousel.addEventListener('scrollend', onScrollEnd, { passive: true });
    carousel.addEventListener('carousel:change', onCarouselChange);
    carousel.addEventListener('pointerdown', () => carousel.player?.pause(), { passive: true });
    carousel.addEventListener('pointerup', () => carousel.player?.resume(true), { passive: true });
    carousel.addEventListener('pointercancel', () => carousel.player?.resume(true), { passive: true });
    mobileMedia.addEventListener('change', onBreakpointChange);

    if (isNative()) onBreakpointChange();
  };

  const setupNativeCarousels = (root = document) => {
    root.querySelectorAll('text-with-icons-carousel, testimonial-carousel').forEach((carousel) => {
      window.customElements.whenDefined(carousel.localName).then(() => setupNativeCarousel(carousel));
    });
  };

  setupNativeCarousels();
  document.addEventListener('shopify:section:load', (event) => setupNativeCarousels(event.target));
})();
